// OAuth utility functions for Google and Microsoft authentication

import { PublicClientApplication, Configuration, AuthenticationResult } from '@azure/msal-browser';

// Google OAuth configuration
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

// Microsoft OAuth configuration
const MICROSOFT_CLIENT_ID = process.env.REACT_APP_MICROSOFT_CLIENT_ID || '';
const MICROSOFT_TENANT_ID = 'common'; // Force to 'common' for multitenant support

// Microsoft MSAL configuration
const msalConfig: Configuration = {
  auth: {
    clientId: MICROSOFT_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${MICROSOFT_TENANT_ID}`,
    redirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
};

let msalInstance: PublicClientApplication | null = null;
let msalInitialized = false;

// Initialize Microsoft MSAL
export const initializeMSAL = async (): Promise<PublicClientApplication | null> => {
  if (!msalInstance && MICROSOFT_CLIENT_ID) {
    msalInstance = new PublicClientApplication(msalConfig);
    await msalInstance.initialize();
    msalInitialized = true;
  }
  return msalInstance;
};

// Get initialized MSAL instance
const getMSALInstance = async (): Promise<PublicClientApplication> => {
  if (!msalInstance || !msalInitialized) {
    const instance = await initializeMSAL();
    if (!instance) {
      throw new Error('Failed to initialize Microsoft authentication');
    }
    return instance;
  }
  return msalInstance;
};

// Google OAuth types
interface GoogleUser {
  email: string;
  given_name?: string;
  family_name?: string;
  sub: string;
}

// Microsoft OAuth types
interface MicrosoftUser {
  mail?: string;
  userPrincipalName?: string;
  givenName?: string;
  surname?: string;
  id: string;
}

// Unified OAuth user interface
export interface OAuthUser {
  email: string;
  firstName?: string;
  lastName?: string;
  providerId: string;
  provider: 'google' | 'microsoft';
}

// Google OAuth login
export const signInWithGoogle = (): Promise<OAuthUser> => {
  return new Promise((resolve, reject) => {
    if (!GOOGLE_CLIENT_ID) {
      reject(new Error('Google Client ID not configured'));
      return;
    }

    // Load Google Identity Services
    if (!window.google) {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.onload = () => initializeGoogleSignIn(resolve, reject);
      script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
      document.head.appendChild(script);
    } else {
      initializeGoogleSignIn(resolve, reject);
    }
  });
};

const initializeGoogleSignIn = (resolve: (user: OAuthUser) => void, reject: (error: Error) => void) => {
  window.google.accounts.id.initialize({
    client_id: GOOGLE_CLIENT_ID,
    callback: (response: any) => {
      try {
        // Decode the JWT token
        const payload = JSON.parse(atob(response.credential.split('.')[1])) as GoogleUser;

        const user: OAuthUser = {
          email: payload.email,
          firstName: payload.given_name,
          lastName: payload.family_name,
          providerId: payload.sub,
          provider: 'google'
        };

        resolve(user);
      } catch (error) {
        reject(new Error('Failed to parse Google response'));
      }
    },
    auto_select: false,
    cancel_on_tap_outside: true,
  });

  // Trigger the sign-in flow
  window.google.accounts.id.prompt((notification: any) => {
    if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
      // Fallback to popup
      window.google.accounts.id.renderButton(
        document.createElement('div'),
        {
          theme: 'outline',
          size: 'large',
          type: 'standard',
        }
      );

      // Create a temporary button and click it
      const tempButton = document.createElement('div');
      tempButton.style.display = 'none';
      document.body.appendChild(tempButton);

      window.google.accounts.id.renderButton(tempButton, {
        theme: 'outline',
        size: 'large',
        type: 'standard',
        click_listener: () => {
          window.google.accounts.id.prompt();
        }
      });

      // Simulate click
      const button = tempButton.querySelector('div[role="button"]') as HTMLElement;
      if (button) {
        button.click();
      }

      // Clean up
      setTimeout(() => {
        document.body.removeChild(tempButton);
      }, 100);
    }
  });
};

// Microsoft OAuth login
export const signInWithMicrosoft = async (): Promise<OAuthUser> => {
  if (!MICROSOFT_CLIENT_ID) {
    throw new Error('Microsoft Client ID not configured');
  }

  try {
    const msal = await getMSALInstance();

    const loginRequest = {
      scopes: ['openid', 'profile', 'email', 'User.Read'],
    };

    const response: AuthenticationResult = await msal.loginPopup(loginRequest);

    if (!response.account) {
      throw new Error('No account information received from Microsoft');
    }

    // Get additional user info from Microsoft Graph
    const graphResponse = await fetch('https://graph.microsoft.com/v1.0/me', {
      headers: {
        'Authorization': `Bearer ${response.accessToken}`,
      },
    });

    let userInfo: MicrosoftUser = {
      id: response.account.localAccountId,
      mail: response.account.username,
    };

    if (graphResponse.ok) {
      const graphData = await graphResponse.json();
      userInfo = { ...userInfo, ...graphData };
    }

    const user: OAuthUser = {
      email: userInfo.mail || userInfo.userPrincipalName || response.account.username,
      firstName: userInfo.givenName,
      lastName: userInfo.surname,
      providerId: userInfo.id,
      provider: 'microsoft'
    };

    return user;
  } catch (error) {
    console.error('Microsoft sign-in error:', error);
    throw new Error('Microsoft sign-in failed');
  }
};

// Declare global Google types
declare global {
  interface Window {
    google: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          prompt: (callback?: (notification: any) => void) => void;
          renderButton: (element: HTMLElement, config: any) => void;
        };
      };
    };
  }
}
